/** EN catalog — FE-4: assessments, packages, templates, composition builder. */
export const assessEn: Record<string, string> = {
  'as.you': 'you',
  'as.sectionTitle': 'Assessments',
  'as.sectionSub':
    'Each assessment is a point-in-time document belonging to the record, never to a booking. None is ever rewritten or deleted.',
  'as.new': 'New assessment',
  'as.emptyTitle': 'No assessments yet',
  'as.emptyBody':
    'No assessment locks nothing — booking and creating a package stay open. Start the draft whenever the assessment session takes place.',
  'as.emptyGate':
    'The Initial Assessment is the recommended start of treatment; a package can be created without it.',
  'as.derivedNote':
    '“Assessed” is never a stored label: this list is the truth itself, and no status is ever written onto the record.',
  'as.reassessNote':
    'A reassessment is another instance of the same artifact and never reinterprets the earlier one. The full reassessment cycle is the progress build (FE-7).',
  'as.type.initial': 'Initial assessment',
  'as.type.reassessment': 'Reassessment',
  'as.status.draft': 'Draft',
  'as.status.completed': 'Completed',
  'as.colAssessment': 'Assessment',
  'as.colStatus': 'Status',
  'as.colSigned': 'Signed',
  'as.continue': 'Continue writing',
  'as.view': 'View',
  'as.startedLine': 'Started {date} by {who}',
  'as.signedLine': 'Signed by {who} · {date} — signer and time are fixed.',
  'as.draftStrip': 'Started {date} by {who} — every change is attributed to its editor.',
  'as.amendedChip': '{n, plural, one {# amendment} other {# amendments}}',

  'as.newTitle': 'New assessment',
  'as.newSub':
    'Only the type is needed to start. Everything else — diagnosis, findings, recommendations — is optional and comes in any order.',
  'as.typeInitialBody':
    'The first clinical picture of this cycle — usually after the first assessment session.',
  'as.typeReassessBody':
    'A later look along the way. The label is the assessor’s own clinical judgement; the system never enforces it.',
  'as.selected': 'Selected',
  'as.intakeCard': 'Intake (optional)',
  'as.intakeCardSub':
    'If an intake exists you may link it — even one still in progress. The assessment only references it; nothing is copied.',
  'as.intakeLinked': 'Linked intake',
  'as.intakeNone': '— none —',
  'as.intakeOption': 'Intake — {date} · {status}',
  'as.intakeHelp': 'Optional. An assessment with no intake is entirely normal.',
  'as.startDraft': 'Start the draft',
  'as.startNote':
    'The draft belongs to the record, not to you — any authorised colleague can continue it, and every edit is attributed.',
  'as.noChecklistTitle': 'No checklist, no percentage',
  'as.noChecklistBody':
    'An almost-empty assessment is valid. There is no required-content list, no progress bar, no mandatory section — deliberately.',

  'as.pageTitle': '{type} — {name}',
  'as.draftSub':
    'Everything is optional and saves as it is. Completing and signing is a separate decision, for when the write-up is ready.',
  'as.completedSub':
    'Completed means signed — not locked forever. A later change is a recorded amendment.',
  'as.amendedSub':
    'Completed, then later amended — both truths read side by side; the signed original is never silently rewritten.',
  'as.writeup': 'The clinical write-up',
  'as.signedWriteup': 'The signed write-up',
  'as.diagnosis': 'Clinical impression',
  'as.diagnosisHelp':
    'Your free clinical note — any explanatory detail behind the diagnoses above, or an impression without one. Optional.',
  // ZOY-700: the shared Diagnosis section — one canonical list for every clinic, labels by code.
  'as.dxSection': 'Diagnosis',
  'as.dxSectionHelp':
    'Tick every diagnosis that applies — one shared list for every clinic. A presentation needs its diagnosis; Other takes your own wording. Optional.',
  'as.dxSignedNote': 'The selection as signed.',
  'as.dxNone': 'No diagnosis selected.',
  'as.dxOtherText': 'Other diagnosis',
  'as.dxOtherHelp': 'Your own wording for a diagnosis not on the list.',
  'as.dxGroup.intellectual_disabilities': 'Intellectual Disabilities',
  'as.dx.intellectual_developmental_disorder':
    'Intellectual Developmental Disorder (Intellectual Disability)',
  'as.dx.global_developmental_delay': 'Global Developmental Delay',
  'as.dx.unspecified_intellectual_developmental_disorder':
    'Unspecified Intellectual Developmental Disorder (Intellectual Disability)',
  'as.dxGroup.communication_disorders': 'Communication Disorders',
  'as.dx.language_disorder': 'Language Disorder',
  'as.dx.speech_sound_disorder': 'Speech Sound Disorder',
  'as.dx.childhood_onset_fluency_disorder': 'Childhood-Onset Fluency Disorder (Stuttering)',
  'as.dx.social_pragmatic_communication_disorder': 'Social (Pragmatic) Communication Disorder',
  'as.dx.unspecified_communication_disorder': 'Unspecified Communication Disorder',
  'as.dxGroup.autism_spectrum_disorder': 'Autism Spectrum Disorder',
  'as.dx.autism_spectrum_disorder': 'Autism Spectrum Disorder (ASD)',
  'as.dxGroup.attention_deficit_hyperactivity_disorders':
    'Attention-Deficit/Hyperactivity Disorders',
  'as.dx.adhd': 'Attention-Deficit/Hyperactivity Disorder (ADHD)',
  'as.dx.adhd_predominantly_inattentive': 'Predominantly Inattentive Presentation',
  'as.dx.adhd_predominantly_hyperactive_impulsive':
    'Predominantly Hyperactive/Impulsive Presentation',
  'as.dx.adhd_combined': 'Combined Presentation',
  'as.dx.other_specified_adhd': 'Other Specified Attention-Deficit/Hyperactivity Disorder',
  'as.dx.unspecified_adhd': 'Unspecified Attention-Deficit/Hyperactivity Disorder',
  'as.dxGroup.specific_learning_disorder': 'Specific Learning Disorder (SLD)',
  'as.dx.specific_learning_disorder': 'Specific Learning Disorder (SLD)',
  'as.dx.sld_impairment_in_reading': 'With impairment in reading',
  'as.dx.sld_impairment_in_written_expression': 'With impairment in written expression',
  'as.dx.sld_impairment_in_mathematics': 'With impairment in mathematics',
  'as.dxGroup.motor_disorders': 'Motor Disorders',
  'as.dx.developmental_coordination_disorder': 'Developmental Coordination Disorder (DCD)',
  'as.dx.stereotypic_movement_disorder': 'Stereotypic Movement Disorder',
  'as.dx.tic_disorders': 'Tic Disorders',
  'as.dx.tourettes_disorder': "Tourette's Disorder",
  'as.dx.persistent_motor_or_vocal_tic_disorder':
    'Persistent (Chronic) Motor or Vocal Tic Disorder',
  'as.dx.provisional_tic_disorder': 'Provisional Tic Disorder',
  'as.dx.other_specified_tic_disorder': 'Other Specified Tic Disorder',
  'as.dx.unspecified_tic_disorder': 'Unspecified Tic Disorder',
  'as.dxGroup.other_neurodevelopmental_disorders': 'Other Neurodevelopmental Disorders',
  'as.dx.other_specified_neurodevelopmental_disorder':
    'Other Specified Neurodevelopmental Disorder',
  'as.dx.unspecified_neurodevelopmental_disorder': 'Unspecified Neurodevelopmental Disorder',
  'as.dxGroup.other': 'Other',
  'as.dx.other': 'Other (free text)',
  'as.amend.dxOtherEmpty': '(none)',
  'as.outcomes': 'Expected outcomes',
  'as.outcomesHelp':
    'Free text — not structured Goals; a Goal/Progress model is the progress build’s question.',
  'as.narrative': 'Assessor narrative',
  'as.narrativeHelp': 'Optional — the session in your own words.',
  'as.saveWriteup': 'Save the write-up',
  'as.savedKept': 'Saved. Nothing is required; empty stays empty.',

  'as.findings': 'Findings',
  'as.findingsSub':
    'Your observations, in your words — no default rows, no instruments, no scores.',
  'as.colName': 'Name',
  'as.colValue': 'Value',
  'as.colUnit': 'Unit',
  'as.findingsFoot':
    'A recorded finding stays on the assessment — rows can be edited, and removing one is not offered in this version.',
  'as.addFinding': 'Add a finding',
  'as.newFinding': 'New finding',
  'as.editFinding': 'Edit finding',
  'as.nameHelp': 'The only required part.',
  'as.valueHelp': 'Plain text — the way you would say it, not a numeric metric.',
  'as.notes': 'Notes',
  'as.addFindingCta': 'Add the finding',
  'as.addFindingNote': 'Added to this assessment, attributed to you.',
  'as.noFindings': 'No findings written yet.',
  'as.noTemplatesTitle': 'No templates, no default rows',
  'as.noTemplatesBody':
    'No instrument list, no preset vocabulary — deliberately. A clinical taxonomy the system does not have is not invented here.',

  'as.recs': 'Service recommendations',
  'as.recsSub':
    'A clinical proposal, not the treatment commitment — no price, no times, no booking. The package is a later, separate decision.',
  'as.colService': 'Service',
  'as.colProposed': 'Proposed sessions',
  'as.proposed': '{n, plural, one {# proposed} other {# proposed}}',
  'as.proposedNone': 'No count proposed',
  'as.recsFoot':
    'Each service appears at most once per assessment. Like findings, a recorded recommendation is not removed in this version.',
  'as.addRec': 'Add a recommendation',
  'as.newRec': 'New recommendation',
  'as.editRec': 'Edit recommendation',
  'as.service': 'Service',
  'as.serviceHelp':
    'From the clinic’s enabled services. Services this assessment already recommends are not offered again.',
  'as.noServiceLeft': 'Every enabled service is already recommended.',
  'as.proposedSessions': 'Proposed sessions',
  'as.proposedHelp': 'Optional — a proposal, never a fixed count.',
  'as.addRecCta': 'Add the recommendation',
  'as.addRecNote': 'A recorded proposal can later start a package — it never binds one.',
  'as.noRecs': 'No recommendations yet.',
  'as.recDisabledNow': 'Now disabled at this clinic — the row stays readable as written.',
  'as.noPriceTitle': 'No price, no times here',
  'as.noPriceBody':
    'Pricing, discounts and scheduling belong to packages and the calendar. A recommendation only says what, and roughly how much, clinically.',
  'as.dupRec': 'That service is already recommended by this assessment.',
  'as.serviceDisabledErr': 'That service is disabled at this clinic and cannot be recommended.',

  'as.intakeContextTitle': 'Intake context',
  'as.intakeContextSub':
    'What the family brought before the assessment — a live reference to the intake itself, not a frozen copy of it. Each line names its source.',
  'as.intakeLinkedChip': 'Linked to this assessment',
  'as.intakeLiveNote':
    'An in-progress intake can be linked; its state always shows here, and it may be completed later.',
  'as.openIntake': 'Open the intake',
  'as.referencedTitle': 'Referenced, never copied',
  'as.referencedBody':
    'This page shows the intake as it is now. If the intake is later amended, the same amendment shows here — and the family’s original words stay reconstructible, as always.',
  'as.noAutofillTitle': 'Nothing auto-fills',
  'as.noAutofillBody':
    'Intake never drafts a diagnosis or a conclusion. The clinical impression is the assessor’s to write.',
  'as.noneRecorded': 'None recorded.',

  'as.completeCta': 'Complete & sign…',
  'as.whoCanSignTitle': 'Who can sign',
  'as.whoCanSignBody':
    'A holder of the complete permission whom the clinic has marked qualified to assess — each condition checked separately.',
  'as.youHoldBoth': 'You hold both.',
  'as.qualificationUnknown':
    'Your qualified-to-assess marking is not readable from here; the clinic system checks it when you sign.',
  'as.completeTitle': 'Complete and sign this assessment',
  'as.completeBody':
    'Signed as {who}; the time is set by the system. Empty sections stay empty — nothing is required.',
  'as.fact1': 'The status becomes completed and never returns to draft.',
  'as.fact2': 'The signer and the signing time stay fixed forever.',
  'as.fact3': 'Package creation opens for {name} — the packages rule.',
  'as.fact4': 'Later changes are added as recorded amendments, never as rewriting.',
  'as.completeNote':
    'There is no counter-signature and no second review — one responsible assessor, one signature.',
  'as.notYet': 'Not yet',
  'as.signCta': 'Sign and complete',
  'as.notQualified':
    'The clinic system did not accept this signature: this account is not marked qualified to assess at this clinic. Nothing changed.',
  'as.alreadyCompleted': 'This assessment was already signed — the page has been refreshed.',

  'as.amend': 'Amend…',
  'as.amending':
    'Amending — each change is recorded after signing under your name; the signed original stays reconstructible.',
  'as.amendDone': 'Done amending',
  'as.gateOpenTitle': 'Assessment signed',
  'as.gateOpenBody':
    'Its recommendations can start {name}’s package — the package’s composition stays freely editable.',
  'as.toPackage': 'Start a package from these recommendations',

  'as.ledgerTitle': 'This document’s history',
  'as.ledgerSub':
    'An amendment is honestly named a change after signing; the signed text stays reconstructible.',
  'as.ledger.created': 'Draft started',
  'as.ledger.signed': 'Signed',
  'as.ledger.amendment': 'Amendment',
  'as.ledgerCreated': 'Assessment draft started.',
  'as.ledgerSigned': 'Assessment completed and signed.',
  'as.amend.title': 'Amendments after signing',
  'as.amend.sub':
    'The signed version above is shown as it was signed. Each change made after signing is listed here, with who made it and when.',
  'as.amend.change': '{before} → {after}',
  'as.amend.empty': '(empty)',
  'as.amend.noteOn': 'Note on “{label}”',
  'as.amend.unknownQuestion': 'A question no longer in this form',
  'as.amend.unreadable': '(not readable)',
  'as.amend.field': 'Another field',
  'as.amend.findingAdded': 'Finding added after signing',
  'as.amend.findingRemoved': 'Finding removed after signing',
  'as.amend.recAdded': 'Recommendation added after signing',
  'as.amend.recRemoved': 'Recommendation removed after signing',
  'as.amend.reason': 'Reason: {reason}',
  'as.amend.unknownService': 'A service no longer in the catalogue',
  'as.amendedState': 'Amended after signing',
  'as.amendedSubLimited':
    'Completed, then amended after signing. What changed, who changed it and when are part of the record history.',
  'as.noSyncTitle': 'Amendments never sync into an existing package',
  'as.noSyncBody':
    'If a package was started from this assessment, a later amendment changes nothing in it — the package holds its own separate decision.',
  'as.noDeleteNote':
    'Assessments are never deleted or voided — drafts included. No such action exists.',

  'path.assessmentOpen': 'Open',
  'path.assessmentNew': 'New assessment',
  'path.packageNew': 'New package',
  'path.packageDone': '{n, plural, one {# package} other {# packages}}',

  'pk.sectionTitle': 'Packages',
  'pk.new': 'New package',
  'pk.emptyTitle': 'No packages yet',
  'pk.emptyBody':
    'A package is the operational and money decision of treatment; an assessment first is recommended, not required.',
  'pk.state.in_use': 'In use',
  'pk.state.stopped': 'Stopped',
  'pk.state.completed': 'Completed',
  'pk.servicesSessions':
    '{services, plural, one {# service} other {# services}} · {sessions, plural, one {# session} other {# sessions}}',
  'pk.total': 'Package total',
  'pk.open': 'Open',

  'pk.newTitle': 'New package for {name}',
  'pk.newSub':
    'Several packages may exist side by side, and the same service may appear in more than one of them.',
  'pk.gateOkTitle': 'Assessed — {type}, {date}',
  'pk.gateOkBody': '{name}’s assessment recommendations can start this package.',
  'pk.gateNoTitle': 'A package before the Initial Assessment',
  'pk.gateNoBody':
    '{name} has no signed assessment yet. The Initial Assessment is recommended, not required — build the package from a template or by hand.',
  'pk.viewAssessment': 'View the assessment',
  'pk.start.manual': 'Build manually',
  'pk.start.manualBody': 'Start empty and add services one by one.',
  'pk.start.template': 'From a template',
  'pk.start.templateBody':
    'Start from a template — its composition and package price come in for you to review.',
  'pk.start.assessment': 'From the assessment',
  'pk.start.assessmentNone': 'This record has no completed assessment.',
  'pk.start.assessmentBody':
    'Prefilled from what the assessment recommended. A suggestion to review — not a prescription.',
  'pk.noTemplates': 'No templates at this clinic yet.',
  'pk.noRecs': 'The signed assessment recommends no services — nothing to prefill.',
  'pk.pickTemplate': 'Template',
  'pk.pickAssessment': 'Assessment',
  'pk.name': 'Package name',
  'pk.nameHelp':
    'Prefilled with a sensible default — starting from a template brings the template’s name instead. Edit it any way you like before saving.',
  'pk.defaultName': 'Package — {name}',
  'pk.explicitPrice': 'Package price',
  'pk.explicitPriceHelp':
    'Optional — an offer price instead of sessions × unit price. A template’s own price arrives here; leave empty to price from the unit prices.',
  'pk.discount': 'Package discount',
  'pk.discountNone': 'None',
  'pk.discountFixed': 'Fixed amount',
  'pk.discountPercent': 'Percent',
  'pk.discountValue': 'Discount value',
  'pk.discountHelp':
    'One package-level discount — fixed or percent. Payment bookkeeping does not live here.',
  'pk.problem.negative': 'A discount cannot be negative.',
  'pk.problem.percentOutOfRange': 'A percentage discount cannot exceed 100.',
  'pk.problem.fixedExceedsGross': 'A fixed discount cannot exceed the package’s gross.',
  'pk.totals.sessions': 'Sessions',
  'pk.totals.before': 'Before discount',
  'pk.totals.discount': 'Discount',
  'pk.totals.total': 'Package total',
  'pk.previewNote':
    'Totals preview what the clinic system will record; the saved amounts are its own.',
  'pk.createCta': 'Create the package',
  'pk.createNote':
    'Unit prices are snapshotted now — a later catalogue price change never rewrites this package.',
  'pk.notAccountingTitle': 'This is not accounting',
  'pk.notAccountingBody':
    'The package records the plan and its price facts. Who paid what and when is the payments surface, and this page does not pretend otherwise.',
  'pk.concurrentTitle': 'Concurrent packages are normal',
  'pk.concurrentBody':
    '{name} may hold another package that also contains the same service. No warning exists for that — it is the clinic’s ordinary reality.',
  'pk.prefillNote':
    'Proposals never bind: the package may differ from them, or not start from them at all. A later assessment amendment never syncs into this package on its own.',
  'pk.noPlanTitle': 'There is no separate Treatment Plan',
  'pk.noPlanBody':
    'In v1, treatment planning is exactly this: assessment recommendations → review → optional package.',
  'pk.err.duplicate': 'A service can appear once per package.',
  'pk.err.disabled': 'One of the services is disabled at this clinic.',
  'pk.err.discount': 'The discount is not valid against this total.',
  'pk.err.composition': 'A package needs a name and at least one service.',
  'pk.err.plannedBelow':
    'The planned count cannot drop below the sessions already delivered. Nothing changed.',

  'pk.facts.provenance': 'Started',
  'pk.prov.assessment': 'from the assessment',
  'pk.prov.template': 'from a template',
  'pk.prov.manual': 'built manually',
  'pk.facts.discount': 'Discount',
  'pk.facts.total': 'Package total',
  'pk.facts.gross': 'Before discount',
  'pk.headNote':
    'Progress counts sessions, not money. Payment status changes no number on this page.',
  'pk.progressTitle': 'Consumption',
  'pk.progressSummary': '{delivered} of {planned} delivered',
  'pk.progress.planned': 'Planned',
  'pk.progress.scheduled': 'Scheduled',
  'pk.progress.delivered': 'Delivered',
  'pk.progress.remaining': 'Remaining to schedule',
  'pk.course.title': 'Course',
  'pk.course.sub':
    'Every appointment attributed to this package, in order, with what became of it. Only a delivered session moves the meter.',
  'pk.course.kind.delivered': 'Delivered',
  'pk.course.kind.voided': 'Voided — not counted',
  'pk.course.kind.in_progress': 'In progress',
  'pk.course.kind.scheduled': 'Scheduled',
  'pk.course.kind.cancelled': 'Cancelled — took nothing',
  'pk.course.kind.no_show': 'No-show — took nothing',
  'pk.course.kind.incomplete': 'Incomplete — took nothing',
  'pk.course.sessionN': 'Session {n} of {planned}',
  'pk.course.empty': 'No appointment attributed to this line yet.',
  'pk.course.unreadable': 'Seeing the course needs the bookings permission.',
  'pk.course.stillScheduled': 'its booking is still live and counts as scheduled',
  'pk.consumeTitle': 'What consumes a session — and what never does',
  'pk.consumeBody':
    'Only a delivered session consumes one. A cancelled session or a no-show takes nothing from the package; rescheduling merely moves a session, it never consumes another; and payment has no effect on these counts.',
  'pk.editTitle': 'Name, price and discount',
  'pk.editNote':
    'Edits are attributed and ledgered with prior values. Editing never completes or closes a package.',
  'pk.clearPrice': 'Leave empty to price from the unit prices again.',
  'pk.lines': 'Services in this package',
  'pk.lineSnapshot': 'Snapshot unit price',
  'pk.addLine': 'Add a service line',
  'pk.changePlanned': 'Change planned count',
  'pk.plannedHelp': 'Never below what was already delivered.',
  'pk.lineNote':
    'Adding a line or raising a count to a completed package returns it to In use by itself — editing is never locked by completion.',
  'pk.lifecycleTitle': 'Lifecycle',
  'pk.life.byEnding': 'By End of treatment',
  'pk.life.derived': 'Derived',
  'pk.life.in_use': 'In use',
  'pk.life.in_useBody':
    'Sessions can be scheduled and delivered against it. The default state after creation.',
  'pk.life.completed': 'Completed',
  'pk.life.completedBody':
    'A live, derived condition — not a lock: the package shows as completed while every planned session is delivered. Raise a planned count or add a line later and it returns to In use by itself.',
  'pk.life.stopped': 'Stopped',
  'pk.life.stoppedBody':
    'Stopped by recording an End of treatment — a dropout or a discharge — for the patient, this service or this package. Nothing new can be booked on it, and its future bookings were cancelled. If the patient returns, “resume package” on the ending reopens it.',
  'pk.understandTitle': 'What the user must understand',
  'pk.understandBody':
    'The counts and activity stay visible always; a completed package comes back by itself if its plan grows; and nothing is deleted — the consumption history stays where it is.',
  'pk.validityTitle': 'Validity dates — a separate proposal, not existing behaviour',
  'pk.validityBody':
    'Start, end or expiry dates do not exist today, and this page does not pretend they do.',
  'pk.terms': 'Payment terms',
  'pk.terms.per_session': 'Per session',
  'pk.terms.upfront_expected': 'Upfront expected',
  'pk.termsHelp':
    'Upfront expected never blocks — booking and delivery warn while less than the package total has been earmarked to it.',
  'pk.progress.overScheduled': 'Booked beyond the plan',
  'pk.progress.overDelivered': 'Delivered beyond the plan',
  'pk.repriceTitle.raised': 'The plan was raised — undelivered slots take the new amounts',
  'pk.repriceTitle.reduced':
    'The plan was reduced — the remaining undelivered slots take the new amounts',
  'pk.repriceTitle.unchanged':
    'The planned count is unchanged — undelivered slots take the new amounts',
  'pk.repriceBody':
    '{frozen, plural, one {# delivered session keeps its amount} other {# delivered sessions keep their amounts}} · {repriced, plural, one {# undelivered slot repriced} other {# undelivered slots repriced}} · package total {before} → {after}.',
  'pk.repriceWithin':
    '{n, plural, one {# earlier over-plan delivery is} other {# earlier over-plan deliveries are}} now within the plan; their catalogue amounts do not change.',
  'pk.fundTitle': 'Funding',
  'pk.perSession': 'Per-session amounts',
  'pk.perSessionSub':
    'The effective payable amount of each planned session under the current discount — allocated exactly by the clinic system. Snapshotted onto a session at delivery.',
  'pk.perSessionNone': 'No sessions have been priced on this package yet.',
  'pk.perSessionCount': '{n} sessions',
  'pk.sessionN': 'Session {n}',
  'pk.base': 'base {amount}',

  'cb.title': 'The package’s composition',
  'cb.subAssess':
    'Prefilled from the assessment’s recommendations — adjust anything before saving.',
  'cb.subTemplate': 'Prefilled from the template — adjust anything before saving.',
  'cb.subManual': 'Add services one by one; each appears once.',
  'cb.chipAssess': 'Prefilled from the assessment',
  'cb.chipTemplate': 'Prefilled from the template',
  'cb.addService': 'Add a service',
  'cb.service': 'Service',
  'cb.planned': 'Planned sessions',
  'cb.unitPrice': 'Unit price',
  'cb.lineTotal': 'Line total',
  'cb.suggested': 'Assessment suggested {n}',
  'cb.suggestedNone': 'Assessment proposed no count',
  'cb.changed': 'set to {n} here',
  'cb.fromTemplate': 'Template: {n}',
  'cb.remove': 'Remove',
  'cb.noLines': 'No services yet.',
  'cb.skipped': 'Skipped — disabled at this clinic: {names}',
  'cb.noPrice': 'no price configured — set one in Clinic setup',
  'cb.allAdded': 'Every enabled service is already in the composition.',
  'pk.unpricedTitle': 'A price is missing',
  'pk.unpricedBody':
    'These services have no price configured, so this package cannot be created yet: {names}. Set a price for each in Clinic setup → Services (0 records a deliberately free service), then come back.',
  'pk.err.unpricedNamed':
    '{name} has no price configured, so the package was refused. Set a price in Clinic setup → Services (0 records a deliberately free service).',
  'pk.err.unpriced':
    'One of the services has no price configured, so the package was refused. Set a price in Clinic setup → Services (0 records a deliberately free service).',

  'tpl.tab': 'Package templates',
  'tpl.title': 'Package templates',
  'tpl.sub':
    'Reusable starting points: a name, services and a session count each — and, when the clinic sells a bundle, the template’s own package price. Service unit prices are still snapshotted when a real package is created.',
  'tpl.new': 'New template',
  'tpl.emptyTitle': 'No templates yet',
  'tpl.emptyBody': 'Packages can still be built by hand or from an assessment.',
  'tpl.sessions': '{n, plural, one {# session} other {# sessions}}',
  'tpl.priceChip': 'Template price',
  'tpl.lineItem': '{name} — {n, plural, one {# session} other {# sessions}}',
  'tpl.priceLine': 'Template price — {price}',
  'tpl.lifecycleTitle': 'Editing and archiving — proposed, not existing behaviour',
  'tpl.lifecycleBody':
    'Templates can be created and used today. Editing, archiving and versioning are planned — a changed template must never rewrite packages already created from it — and are not available yet.',
  'tpl.createTitle': 'New template',
  'tpl.createSub':
    'A name, services and session counts — plus an optional template price when the template is a commercial bundle.',
  'tpl.name': 'Template name',
  'tpl.nameHelp':
    'Short and descriptive — the way staff will say it out loud, e.g. “5-session At-home Speech Therapy Package”.',
  'tpl.description': 'Description',
  'tpl.descHelp': 'Optional — for when the name alone does not say enough.',
  'tpl.price': 'Template price',
  'tpl.priceHelp':
    'Optional — for a commercial bundle whose offer price is not sessions × the unit price. Left empty, a package prices from the services’ unit prices.',
  'tpl.holds': 'What the template holds',
  'tpl.createCta': 'Create the template',
  'tpl.createNote':
    'Session counts live here; service unit prices are snapshotted when a real package is created.',
  'tpl.offerTitle': 'A template price is an offer, not accounting',
  'tpl.offerBody':
    'When a template carries its own price, packages created from it start at that price — and the operator reviews the price and composition before saving.',
  'tpl.created': 'Template created.',
  /* Structured forms (ZOY-538/539) — UI chrome only; all clinical wording
     (titles, sections, questions, options) comes from the compiled
     definition document and is never translated here. */
  'fmt.title': 'Assessment format',
  'fmt.simpleChip': 'Simple — free text',
  'fmt.versionChip': 'Version {n, number}',
  'fmt.subPick':
    'The write-up below is the simple assessment, always available. Choosing a structured format adds a form to this assessment — answers save as you go.',
  'fmt.subHave':
    'This assessment carries the structured form below, pinned to the version it was started on. The free-text write-up stays available alongside it.',
  'fmt.existsRefetched':
    'A form had already been started on this assessment — it is shown below now.',
  'fmt.actionFailed':
    'That didn\u2019t go through — nothing changed. Check the connection and try again.',
  'fmt.noTemplates': 'No structured format is available to pick right now.',
  'fmt.use': 'Use this format',
  'fmt.actionsNote':
    'Changing or discarding sets the current answers aside — they stay in the clinic\u2019s ledger, but leave this assessment.',
  'fmt.switch': 'Change format',
  'fmt.discard': 'Discard the form',
  'fmt.switchTitle': 'Change the assessment format?',
  'fmt.switchBody':
    'The answers entered on \u201c{from}\u201d are set aside and a fresh \u201c{to}\u201d form starts empty.',
  'fmt.consequenceTitle': 'What this does',
  'fmt.switchEffect':
    'Nothing is deleted: the set-aside answers remain in the clinical ledger, but they no longer appear on this assessment and do not carry over to the new form.',
  'fmt.switchCta': 'Change the format',
  'fmt.discardTitle': 'Discard this form?',
  'fmt.discardBody':
    'The \u201c{name}\u201d form and its answers are set aside, and this becomes a simple assessment again.',
  'fmt.discardEffect':
    'Nothing is deleted: the answers remain in the clinical ledger, but they no longer appear on this assessment.',
  'fmt.discardCta': 'Discard and return to simple',

  'sf.sealedChip': 'Sealed at sign-off',
  'sf.sealedSubAmended':
    'Shown exactly as signed. Changes made after signing are listed under “Amendments after signing”.',
  'sf.sealedSubCurrent':
    'Sealed at sign-off. Answers are shown as they stand now — any change made after signing is recorded under the amender’s name.',
  'sf.sealedSub':
    'Shown exactly as captured — wording and answers are frozen with the signed assessment.',
  'sf.unsupportedTitle': 'This form can\u2019t be displayed here',
  'sf.unsupportedBody':
    'It uses a newer form format than this version of the app understands. Nothing about the answers is lost or changed — displaying it needs an updated app.',
  'sf.incompleteTitle': 'Required answers are missing',
  'sf.incompleteBody': 'Sign-off needs every visible required question answered. Jump to each one:',
  'sf.answeredOf': '{answered, number} of {total, number} answered',
  'sf.missingCount': '{n, plural, one {# required left} other {# required left}}',
  'sf.saving': 'Saving…',
  'sf.saved': 'Saved',
  'sf.saveFailed': 'Not saved — your entries are kept here.',
  'sf.retry': 'Try again',
  'sf.yes': 'Yes',
  'sf.no': 'No',
  'sf.clear': 'Clear the answer',
  'sf.note': 'Note',
  'sf.addNote': 'Add a note',
  'sf.noAnswer': 'Not answered',
  'sf.numberInvalid': 'Enter a number.',
  'sf.numberRange': 'Between {min, number} and {max, number}.',
  'sf.numberMin': 'At least {min, number}.',
  'sf.numberMax': 'At most {max, number}.',
  'sf.minSelections':
    '{n, plural, one {Pick at least # option} other {Pick at least # options}} — kept here until then.',
  'sf.maxSelections': '{n, plural, one {At most # option} other {At most # options}}.',
  'sf.incompleteBanner':
    'Not signed — {n, plural, one {# required question is} other {# required questions are}} unanswered in the structured form below.',
  'sf.autosaveFailedBanner':
    'Sign-off stopped: the latest form answers could not be saved. Nothing you entered was lost — retry saving in the form, then sign again.',
  /* Booked assessment association (ZOY-545/552). */
  'as.forBookedTitle': 'For a booked session',
  'as.forBookedBody':
    'This assessment records the booked session it is being started from. The link is set at creation and never changes.',
  'as.err.bookingAssessed':
    'That booked session already has its own assessment — each booked session takes exactly one.',
  'as.err.bookingNotAssessment':
    'The booked service does not declare the \u201cassessment\u201d workflow, so an assessment cannot attach to it.',
  'as.err.bookingNotActive':
    'That booking is no longer active, so it has no clinical work to assess.',
  'as.sessionAssessTitle': 'This session\u2019s assessment',
  'fmt.defaultChip': 'Default',
  /* Typed form-save states (ZOY-538) — only a transient failure may retry. */
  'sf.saveStale':
    'This form is no longer the live one — it was changed or set aside elsewhere. Reopen the assessment to see the current state.',
  'sf.saveRejected':
    'One answer was not accepted, so that change was not saved. Everything on screen is kept — adjust it and it will save again.',
  'sf.saveForbidden': 'Your permission to edit this assessment has ended. Nothing was saved.',
  /* Picker (ZOY-538/544) */
  'fmt.currentChip': 'Current selection',
  'fmt.onlyCurrent': 'This is the only structured format available, and it is already in use here.',
  'fmt.errSignedOff':
    'This assessment has been signed off, so its format can no longer be changed.',
  'fmt.errSealed':
    'This form was sealed with the signed assessment and can no longer be set aside.',
  'fmt.errGone': 'That form is no longer the live one here — the page has been refreshed.',
  'fmt.errUnavailable': 'That format is no longer available to start. The list has been refreshed.',
  /* Booked assessment refusals (ZOY-552) */
  'as.err.bookingNotFound':
    'That booked session could not be found, so nothing was created. Open it again from the calendar and retry.',
  'as.err.bookingMismatch':
    'That booked session belongs to a different patient, so nothing was created.',
};
